import request from '../_helpers/request';
import axios from 'axios';
import { authHeader } from '../_helpers';
import { normalizeApiResponse } from '../_helpers/apiResponse';
import { unwrapBrApiRows } from '../constants/brList';

const SCAN_PATH = 'ApiAi/businesslicense/scan';

function getAiApiBasePath() {
  return String(process.env.VUE_APP_WEBCOREAPI_AI_BASE_PATH || '').replace(/\/?$/, '/');
}

async function callBrApi(data, url) {
  const raw = await request({ url, method: 'post', data });
  return raw && typeof raw.isSuccessful === 'boolean'
    ? raw
    : normalizeApiResponse(raw);
}

function withOptionalEmpCompanyId(payload, data) {
  if (data?.empCompanyId != null && data.empCompanyId !== '') {
    payload.empCompanyId = Number(data.empCompanyId);
  }
  return payload;
}

/** GetList：统一 data.items（兼容 Items / 纯数组） */
function normalizeGetListResponse(res) {
  if (!res?.isSuccessful) {
    return res;
  }
  const rows = unwrapBrApiRows(res.data);
  if (Array.isArray(res.data) || !res.data || typeof res.data !== 'object') {
    return { ...res, data: { items: rows } };
  }
  return {
    ...res,
    data: {
      ...res.data,
      items: rows,
    },
  };
}

/** AdminPending / 更名列表：统一为数组 */
function normalizeRowsResponse(res) {
  if (!res?.isSuccessful) {
    return res;
  }
  return { ...res, data: unwrapBrApiRows(res.data) };
}

export const BusinessRegistrationService = {
  GetSummary: (data = {}) => callBrApi(data, 'BusinessRegistration/GetSummary'),
  GetList: async (data = {}) => normalizeGetListResponse(
    await callBrApi(data, 'BusinessRegistration/GetList'),
  ),
  GetAccessGate: () => callBrApi({}, 'BusinessRegistration/GetAccessGate'),
  SaveDocument: (data) => callBrApi(
    withOptionalEmpCompanyId({
      companyName: data?.companyName ?? '',
      registrationNo: data?.registrationNo ?? '',
      issueDate: data?.issueDate,
      expiryDate: data?.expiryDate,
      mediaFileName: data?.mediaFileName ?? '',
      // 用户本地原文件名；缺省时列表只能回退 OSS 哈希名
      originalFileName: data?.originalFileName ?? '',
      ocrPayloadJson: data?.ocrPayloadJson ?? '',
      ocrCompanyName: data?.ocrCompanyName ?? '',
      ocrRegistrationNo: data?.ocrRegistrationNo ?? '',
      displayNameEn: data?.displayNameEn ?? '',
      displayNameZh: data?.displayNameZh ?? '',
    }, data),
    'BusinessRegistration/SaveDocument',
  ),
  EnqueueForApproval: (data) => callBrApi(
    withOptionalEmpCompanyId({
      businessRegistrationId: Number(data?.businessRegistrationId),
      accountId: data?.accountId != null ? Number(data.accountId) : undefined,
      submitType: data?.submitType || undefined,
    }, data),
    'BusinessRegistration/EnqueueForApproval',
  ),
  AcknowledgeResultNotify: (data = {}) => callBrApi(data, 'BusinessRegistration/AcknowledgeResultNotify'),
  GetPendingNameChange: async () => normalizeRowsResponse(
    await callBrApi({}, 'BusinessRegistration/GetPendingNameChange'),
  ),
  GetAdminPending: async (empCompanyId) => normalizeRowsResponse(
    await callBrApi(
      { empCompanyId: Number(empCompanyId) },
      'BusinessRegistration/GetAdminPending',
    ),
  ),
  RequestDisplayNameChange(data) {
    return callBrApi({
      sourceApprovalId: Number(data?.sourceApprovalId),
      displayNameEn: data?.displayNameEn ?? '',
      displayNameZh: data?.displayNameZh ?? '',
      empCompanyId: data?.empCompanyId != null ? Number(data.empCompanyId) : undefined,
      accountId: data?.accountId != null ? Number(data.accountId) : undefined,
      submitType: data?.submitType || undefined,
    }, 'BusinessRegistration/RequestDisplayNameChange');
  },
  AdminProcess(data) {
    return callBrApi({
      empCompanyId: Number(data?.empCompanyId),
      approvalId: Number(data?.approvalId),
      action: String(data?.action || '').toUpperCase(),
      rejectReason: data?.rejectReason || '',
    }, 'BusinessRegistration/AdminProcess');
  },
  ListAdminNotes: (empCompanyId) => callBrApi(
    { empCompanyId: Number(empCompanyId) },
    'BusinessRegistration/AdminNote/List',
  ),
  CreateAdminNote(data) {
    return callBrApi({
      empCompanyId: Number(data?.empCompanyId),
      remark: String(data?.remark || '').trim(),
      createUser: data?.createUser || '',
      attachments: Array.isArray(data?.attachments) ? data.attachments : [],
    }, 'BusinessRegistration/AdminNote/Create');
  },
  SoftDeleteDocument(data = {}) {
    return callBrApi(
      withOptionalEmpCompanyId(
        { businessRegistrationId: Number(data?.businessRegistrationId) },
        data,
      ),
      'BusinessRegistration/SoftDeleteDocument',
    );
  },
  /** 管理端编辑模式：将 BR 设为无效（已驳回 / 已审批系） */
  AdminInvalidateDocument(data = {}) {
    return callBrApi(
      {
        empCompanyId: Number(data?.empCompanyId),
        businessRegistrationId: Number(data?.businessRegistrationId),
      },
      'BusinessRegistration/AdminInvalidateDocument',
    );
  },
  SetAsCurrent(data = {}) {
    return callBrApi(
      withOptionalEmpCompanyId({ approvalId: Number(data?.approvalId) }, data),
      'BusinessRegistration/SetAsCurrent',
    );
  },
  ScanBusinessLicense,
};

async function ScanBusinessLicense(file) {
  const url = `${getAiApiBasePath()}${SCAN_PATH}`;
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await axios.post(url, formData, {
      headers: { ...authHeader() },
      timeout: 120000,
    });
    return {
      ...normalizeApiResponse(response.data),
      httpStatus: response.status,
    };
  } catch (error) {
    const responseData = error.response?.data;
    return {
      ...normalizeApiResponse(
        responseData && typeof responseData === 'object' && Object.keys(responseData).length
          ? responseData
          : { IsSuccessful: false, Msg: error.message || 'scan failed', Data: null },
      ),
      httpStatus: error.response?.status,
    };
  }
}
